// Table of Contents Component
// Interactive TOC that highlights current section and allows navigation

import React, { useState, useEffect, useRef } from 'react';

interface TOCSection {
  id: string;
  label: string;
  level: number;
}

interface TableOfContentsProps {
  sections: TOCSection[];
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({ sections }) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [isVisible, setIsVisible] = useState(window.innerWidth > 1024);
  const tocRef = useRef<HTMLDivElement>(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsVisible(window.innerWidth > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Find the scrollable container - traverse up from TOC to find the main element
    let scrollContainer = tocRef.current?.parentElement;
    while (scrollContainer && scrollContainer.tagName !== 'MAIN') {
      scrollContainer = scrollContainer.parentElement;
    }

    if (!scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        root: scrollContainer, // Use the actual scrollable container
        rootMargin: '-20px 0px -80% 0px', // Trigger when section is near top
        threshold: 0
      }
    );

    // Observe all sections
    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach(({ id }) => {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [sections]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);

    // Find the scrollable container
    let scrollContainer = tocRef.current?.parentElement;
    while (scrollContainer && scrollContainer.tagName !== 'MAIN') {
      scrollContainer = scrollContainer.parentElement;
    }

    if (element && scrollContainer) {
      // Calculate position relative to the scroll container
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const offset = 20; // Small offset from top

      scrollContainer.scrollTo({
        top: scrollContainer.scrollTop + (elementRect.top - containerRect.top) - offset,
        behavior: 'smooth'
      });
    }
  };

  // Fixed positioning for consistent navbar height
  const topPosition = '150px';
  const maxHeightCalc = 'calc(100vh - 170px)';

  if (!isVisible) return null;

  return (
    <div ref={tocRef} style={{
      position: 'fixed',
      top: topPosition,
      right: '30px',
      width: '250px',
      maxHeight: maxHeightCalc,
      overflowY: 'auto',
      padding: '16px',
      background: 'var(--ifm-background-color)',
      border: '1px solid var(--ifm-color-emphasis-300)',
      borderRadius: '8px',
      fontSize: '14px',
      zIndex: 10
    }}>
      <div style={{
        fontWeight: '600',
        marginBottom: '12px',
        color: 'var(--ifm-font-color-base)',
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        On This Page
      </div>

      <nav>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0
        }}>
          {sections.map(({ id, label, level }) => (
            <li
              key={id}
              style={{
                marginBottom: '6px',
                paddingLeft: `${(level - 1) * 12}px`
              }}
            >
              <a
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(id);
                }}
                style={{
                  display: 'block',
                  padding: '4px 8px',
                  color: activeSection === id ? '#007bff' : 'var(--ifm-color-emphasis-700)',
                  textDecoration: 'none',
                  borderLeft: activeSection === id ? '2px solid #007bff' : '2px solid transparent',
                  paddingLeft: '10px',
                  marginLeft: '-2px',
                  transition: 'all 0.2s ease',
                  fontSize: level === 1 ? '13px' : '12px',
                  fontWeight: activeSection === id ? '600' : level === 1 ? '500' : '400',
                  lineHeight: '1.4'
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== id) {
                    e.currentTarget.style.color = '#007bff';
                    e.currentTarget.style.borderLeftColor = '#007bff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== id) {
                    e.currentTarget.style.color = 'var(--ifm-color-emphasis-700)';
                    e.currentTarget.style.borderLeftColor = 'transparent';
                  }
                }}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};
